import { RevenueMarketEditor } from '@/components/travel/revenue-market';
import { DirectionEditor } from '@/components/travel/direction';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  User,
  Calendar,
  DollarSign,
  Loader2,
  Plus,
  MoreHorizontal,
  Trash2,
  Users,
  Package,
  CreditCard,
  Clock,
  MapPin,
  FileText,
  CheckCircle,
  XCircle,
  Plane,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PassengerModal } from '@/components/travel/bookings/passenger-modal';
import { PaymentModal } from '@/components/travel/bookings/payment-modal';
import { PaymentPlanCard } from '@/components/travel/bookings/payment-plan-card';
import { InstallmentPaymentModal } from '@/components/travel/bookings/installment-payment-modal';
import { BookingItemsCard } from '@/components/travel/bookings/booking-items-card';
import { InvoiceSidebarCard } from '@/components/travel/bookings/invoice-sidebar-card';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-700',
  FULLY_PAID: 'bg-green-100 text-green-700',
  TICKETED: 'bg-purple-100 text-purple-700',
  IN_PROGRESS: 'bg-cyan-100 text-cyan-700',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-pink-100 text-pink-700',
  PARTIALLY_REFUNDED: 'bg-pink-50 text-pink-600',
};

const statusLabelKeys: Record<string, string> = {
  PENDING: 'statusLabels.PENDING',
  CONFIRMED: 'statusLabels.CONFIRMED',
  PARTIALLY_PAID: 'statusLabels.PARTIALLY_PAID',
  FULLY_PAID: 'statusLabels.FULLY_PAID',
  TICKETED: 'statusLabels.TICKETED',
  IN_PROGRESS: 'statusLabels.IN_PROGRESS',
  COMPLETED: 'statusLabels.COMPLETED',
  CANCELLED: 'statusLabels.CANCELLED',
  REFUNDED: 'statusLabels.REFUNDED',
  PARTIALLY_REFUNDED: 'statusLabels.PARTIALLY_REFUNDED',
};

const paymentMethodLabelKeys: Record<string, string> = {
  CASH: 'paymentMethodLabels.CASH',
  CREDIT_CARD: 'paymentMethodLabels.CREDIT_CARD',
  DEBIT_CARD: 'paymentMethodLabels.DEBIT_CARD',
  BANK_TRANSFER: 'paymentMethodLabels.BANK_TRANSFER',
  PAYPAL: 'paymentMethodLabels.PAYPAL',
  STRIPE: 'paymentMethodLabels.STRIPE',
  OTHER: 'paymentMethodLabels.OTHER',
};

type BookingStatus = 'PENDING' | 'CONFIRMED' | 'PARTIALLY_PAID' | 'FULLY_PAID' | 'TICKETED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'REFUNDED';
type PassengerType = 'ADULT' | 'CHILD' | 'INFANT';

type Passenger = {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  nationality: string | null;
  passportNumber: string | null;
  passportExpiry: Date | null;
  passengerType: PassengerType;
  isLeadPassenger: boolean;
  mealPreference: string | null;
  specialNeeds: string | null;
};

type Payment = {
  id: string;
  amount: { toNumber: () => number } | number;
  currency: string;
  paymentMethod: string;
  paymentDate: Date;
  referenceNumber: string | null;
  notes: string | null;
  status: string;
};

export function BookingDetailPage() {
  const { t } = useTranslation('bookings');
  const { id } = useParams();
  const [isPassengerModalOpen, setIsPassengerModalOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState<Passenger | undefined>();
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [installmentPayment, setInstallmentPayment] = useState<{
    installmentId: string;
    description: string;
    amount: number;
  } | null>(null);
  const { user } = useAuthStore();

  const { data: booking, isLoading, error } = trpc.booking.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.booking.updateStatus.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(id);
    },
  });

  const deletePassengerMutation = trpc.booking.deletePassenger.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(id);
    },
  });

  const deletePaymentMutation = trpc.booking.deletePayment.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(id);
    },
  });

  const handleStatusChange = (newStatus: BookingStatus) => {
    if (id) {
      updateStatusMutation.mutate({ id, status: newStatus, userId: user?.id });
    }
  };

  const handleAddPassenger = () => {
    setEditingPassenger(undefined);
    setIsPassengerModalOpen(true);
  };

  const handleEditPassenger = (passenger: Passenger) => {
    setEditingPassenger(passenger);
    setIsPassengerModalOpen(true);
  };

  const handleDeletePassenger = (passengerId: string) => {
    if (confirm(t('confirmRemovePassenger'))) {
      deletePassengerMutation.mutate(passengerId);
    }
  };

  const handleAddPayment = () => {
    setIsPaymentModalOpen(true);
  };

  const handleDeletePayment = (paymentId: string) => {
    if (confirm(t('confirmDeletePayment'))) {
      deletePaymentMutation.mutate({ paymentId, userId: user?.id, reason: 'Payment deleted by user' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('loadingBooking')}</span>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="space-y-4">
        <Link
          to="/travel/bookings"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToBookings')}
        </Link>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('bookingNotFound')}</p>
          <p className="text-sm">{error?.message || t('bookingNotExist')}</p>
        </div>
      </div>
    );
  }

  const totalAmount = typeof booking.totalAmount === 'number' ? booking.totalAmount : booking.totalAmount;
  const amountPaid = typeof booking.amountPaid === 'number' ? booking.amountPaid : booking.amountPaid;
  const balanceDue = typeof booking.balanceDue === 'number' ? booking.balanceDue : booking.balanceDue;
  const paymentProgress = totalAmount > 0 ? (amountPaid / totalAmount) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <Link
        to="/travel/bookings"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Bookings
      </Link>

      <DirectionEditor kind="booking" id={booking.id} value={booking.direction} />
      <RevenueMarketEditor kind="booking" id={booking.id} value={booking.revenueMarket} />
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{booking.bookingReference}</span>
            <span className={`px-2.5 py-0.5 text-xs font-medium ${statusColors[booking.status]}`}>
              {t(statusLabelKeys[booking.status] || booking.status)}
            </span>
          </div>
          <h1 className="text-2xl font-bold">
            {t('bookingFor', { name: `${booking.customer.firstName} ${booking.customer.lastName}` })}
          </h1>
          {booking.package && (
            <Link
              to={`/travel/packages/${booking.package.id}`}
              className="text-primary hover:underline flex items-center gap-1"
            >
              <Package className="h-4 w-4" />
              {booking.package.name}
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                {t('updateStatus')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {booking.status === 'PENDING' && (
                <DropdownMenuItem onClick={() => handleStatusChange('CONFIRMED')}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t('confirmBooking')}
                </DropdownMenuItem>
              )}
              {booking.status === 'FULLY_PAID' && (
                <DropdownMenuItem onClick={() => handleStatusChange('TICKETED')}>
                  <Plane className="mr-2 h-4 w-4" />
                  {t('markTicketed')}
                </DropdownMenuItem>
              )}
              {booking.status === 'TICKETED' && (
                <DropdownMenuItem onClick={() => handleStatusChange('IN_PROGRESS')}>
                  <MapPin className="mr-2 h-4 w-4" />
                  {t('markInProgress')}
                </DropdownMenuItem>
              )}
              {booking.status === 'IN_PROGRESS' && (
                <DropdownMenuItem onClick={() => handleStatusChange('COMPLETED')}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t('markCompleted')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && (
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => handleStatusChange('CANCELLED')}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  {t('cancelBooking')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Passenger Modal */}
      {isPassengerModalOpen && (
        <PassengerModal
          open={isPassengerModalOpen}
          onOpenChange={setIsPassengerModalOpen}
          bookingId={booking.id}
          passenger={editingPassenger}
          onSuccess={() => utils.booking.byId.invalidate(id)}
        />
      )}

      {/* Payment Modal */}
      {isPaymentModalOpen && (
        <PaymentModal
          open={isPaymentModalOpen}
          onOpenChange={setIsPaymentModalOpen}
          bookingId={booking.id}
          currency={booking.currency}
          balanceDue={balanceDue}
          userId={user?.id}
          onSuccess={() => utils.booking.byId.invalidate(id)}
        />
      )}

      {/* Installment Payment Modal */}
      {installmentPayment && (
        <InstallmentPaymentModal
          open={!!installmentPayment}
          onOpenChange={(open) => !open && setInstallmentPayment(null)}
          installmentId={installmentPayment.installmentId}
          installmentDescription={installmentPayment.description}
          remainingAmount={installmentPayment.amount}
          currency={booking.currency}
          userId={user?.id}
          onSuccess={() => utils.booking.byId.invalidate(id)}
        />
      )}

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Booking Info Card */}
          <div className="border border-border bg-card p-6">
            <h2 className="mb-4 font-semibold">{t('bookingDetails')}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('travelDates')}</p>
                  <p className="font-medium">
                    {new Date(booking.travelStartDate).toLocaleDateString()} -{' '}
                    {new Date(booking.travelEndDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('travelers')}</p>
                  <p className="font-medium">
                    {booking.totalAdults} {booking.totalAdults > 1 ? t('adults_plural') : t('adults')}
                    {booking.totalChildren > 0 && `, ${booking.totalChildren} ${booking.totalChildren > 1 ? t('children_plural') : t('children')}`}
                    {booking.totalInfants > 0 && `, ${booking.totalInfants} ${booking.totalInfants > 1 ? t('infants_plural') : t('infants')}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('bookedOn')}</p>
                  <p className="font-medium">
                    {new Date(booking.bookingDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('currency')}</p>
                  <p className="font-medium">{booking.currency}</p>
                </div>
              </div>
            </div>
            {booking.specialRequests && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">{t('specialRequests')}</p>
                <p className="text-sm whitespace-pre-wrap">{booking.specialRequests}</p>
              </div>
            )}
            {booking.internalNotes && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">{t('internalNotes')}</p>
                <p className="text-sm whitespace-pre-wrap bg-yellow-50 p-2 border border-yellow-200">{booking.internalNotes}</p>
              </div>
            )}
          </div>

          {/* Booking Items with Allocations */}
          <BookingItemsCard
            bookingId={booking.id}
            items={booking.items || []}
            defaultServiceDate={new Date(booking.travelStartDate)}
            defaultEndDate={new Date(booking.travelEndDate)}
            onSuccess={() => utils.booking.byId.invalidate(id)}
          />

          {/* Passengers */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('passengersCount', { count: booking.passengers?.length || 0 })}
              </h2>
              <Button variant="outline" size="sm" onClick={handleAddPassenger}>
                <Plus className="mr-2 h-4 w-4" />
                {t('addPassenger')}
              </Button>
            </div>
            {booking.passengers && booking.passengers.length > 0 ? (
              <div className="space-y-3">
                {booking.passengers.map((passenger: Passenger) => (
                  <div key={passenger.id} className="border border-border p-4 group">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">
                            {passenger.firstName} {passenger.lastName}
                          </span>
                          <span className={`px-2 py-0.5 text-xs ${
                            passenger.passengerType === 'ADULT' ? 'bg-blue-100 text-blue-700' :
                            passenger.passengerType === 'CHILD' ? 'bg-green-100 text-green-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {passenger.passengerType}
                          </span>
                          {passenger.isLeadPassenger && (
                            <span className="bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                              {t('lead')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {passenger.nationality && (
                            <p>{t('nationality')}{passenger.nationality}</p>
                          )}
                          {passenger.passportNumber && (
                            <p>
                              {t('passport')}{passenger.passportNumber}
                              {passenger.passportExpiry && (
                                <span className="ml-2">
                                  {t('passportExpiry', { date: new Date(passenger.passportExpiry).toLocaleDateString() })}
                                </span>
                              )}
                            </p>
                          )}
                          {passenger.dateOfBirth && (
                            <p>{t('dob')}{new Date(passenger.dateOfBirth).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditPassenger(passenger)}>
                            <FileText className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeletePassenger(passenger.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('noPassengers')}</p>
            )}
          </div>

          {/* Payments */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {t('paymentsCount', { count: booking.payments?.length || 0 })}
              </h2>
              {balanceDue > 0 && (
                <Button variant="outline" size="sm" onClick={handleAddPayment}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('recordPayment')}
                </Button>
              )}
            </div>
            {booking.payments && booking.payments.length > 0 ? (
              <div className="space-y-3">
                {booking.payments.map((payment: Payment) => {
                  const paymentAmount = typeof payment.amount === 'number'
                    ? payment.amount
                    : typeof payment.amount === 'object' && payment.amount !== null && 'toNumber' in payment.amount
                      ? (payment.amount as { toNumber: () => number }).toNumber()
                      : Number(payment.amount);
                  return (
                    <div key={payment.id} className="border border-border p-4 group">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">
                              {formatCurrency(paymentAmount)}
                            </span>
                            <span className="bg-muted px-2 py-0.5 text-xs">
                              {paymentMethodLabelKeys[payment.paymentMethod] ? t(paymentMethodLabelKeys[payment.paymentMethod]!) : payment.paymentMethod}
                            </span>
                            <span className={`px-2 py-0.5 text-xs ${
                              payment.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                              payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {payment.status}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(payment.paymentDate).toLocaleDateString()}
                            {payment.referenceNumber && ` • ${t('paymentRef')}${payment.referenceNumber}`}
                          </p>
                          {payment.notes && (
                            <p className="text-sm text-muted-foreground mt-1">{payment.notes}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeletePayment(payment.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('noPayments')}</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Card */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold flex items-center gap-2 mb-4">
              <User className="h-5 w-5" />
              {t('customerLabel')}
            </h2>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">
                  {booking.customer.firstName} {booking.customer.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{booking.customer.email}</p>
              </div>
            </div>
            {booking.customer.phone && (
              <p className="text-sm text-muted-foreground mb-2">
                {t('phone')}{booking.customer.phone}
              </p>
            )}
            <Link
              to={`/travel/cari/accounts/customer/${booking.customer.id}`}
              className="text-sm text-primary hover:underline"
            >
              {t('viewCustomerProfile')} →
            </Link>
          </div>

          {/* Payment Summary */}
          <div className="border border-border bg-card p-6">
            <h2 className="font-semibold flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5" />
              {t('paymentSummary')}
            </h2>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">{t('paymentProgress')}</span>
                <span className="font-medium">{Math.round(paymentProgress)}%</span>
              </div>
              <div className="h-2 bg-muted overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    paymentProgress >= 100 ? 'bg-green-500' :
                    paymentProgress > 0 ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                  style={{ width: `${Math.min(100, paymentProgress)}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('totalAmount')}</span>
                <span className="font-medium">{formatCurrency(totalAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('amountPaid')}</span>
                <span className="font-medium text-green-600">{formatCurrency(amountPaid)}</span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between font-medium">
                <span>{t('balanceDue')}</span>
                <span className={balanceDue > 0 ? 'text-red-600' : 'text-green-600'}>
                  {formatCurrency(balanceDue)}
                </span>
              </div>
            </div>

            {balanceDue > 0 && (
              <Button className="w-full mt-4" onClick={handleAddPayment}>
                <CreditCard className="mr-2 h-4 w-4" />
                Record Payment
              </Button>
            )}
          </div>

          {/* Invoice Sidebar Card */}
          <InvoiceSidebarCard
            bookingId={booking.id}
            currency={booking.currency}
          />

          {/* Payment Plan */}
          <PaymentPlanCard
            bookingId={booking.id}
            totalAmount={totalAmount}
            currency={booking.currency}
            travelStartDate={booking.travelStartDate.toString()}
            paymentPlan={booking.paymentPlan}
            onSuccess={() => utils.booking.byId.invalidate(id)}
            onRecordPayment={(installmentId, amount) => {
              const installment = booking.paymentPlan?.installments.find(
                (i) => i.id === installmentId
              );
              if (installment) {
                setInstallmentPayment({
                  installmentId,
                  description: installment.description,
                  amount,
                });
              }
            }}
          />

          {/* Package Itinerary Preview */}
          {booking.package?.itinerary && booking.package.itinerary.length > 0 && (
            <div className="border border-border bg-card p-6">
              <h2 className="font-semibold flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5" />
                {t('itinerary')}
              </h2>
              <div className="space-y-2">
                {booking.package.itinerary.slice(0, 5).map((day: { id: string; dayNumber: number; title: string }) => (
                  <div key={day.id} className="flex items-start gap-2 text-sm">
                    <span className="flex h-5 w-5 items-center justify-center bg-primary/10 text-xs text-primary flex-shrink-0">
                      {day.dayNumber}
                    </span>
                    <span className="text-muted-foreground">{day.title}</span>
                  </div>
                ))}
                {booking.package.itinerary.length > 5 && (
                  <Link
                    to={`/travel/packages/${booking.package.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('viewFullItinerary', { count: booking.package.itinerary.length })} →
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
